import { useContext } from 'react'
import isMobileBrowser from 'is-mobile'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faClockRotateLeft, faHome } from '@fortawesome/pro-light-svg-icons'

import { pathWithRedirect } from '@domain-group/javascript-utils'

import {
  Box, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import type { CampaignModel } from '@models/types'

import useActivities from '@hooks/useActivities'
import useCampaign from '@hooks/useCampaign'

import ActivityItem from '@components/ActivityListItem'
import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import defaultRequestOptions from '@sections/Client/packs/Campaign/defaultRequestOptions'

import ActivityBlankState from '../BlankState'

const isMobile = isMobileBrowser()

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'created_date_start',
      label: 'Activity After',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'created_date_end',
      label: 'Activity Before',
    },
  ]
}

interface PageContentProps {
  campaign: CampaignModel,
  pageFilters: {},
}

const PageContent = (props: PageContentProps) => {
  const { campaign: initCampaign, pageFilters } = props

  useLocation()

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const campaignPayload = useCampaign(initCampaign)
  const { campaign, campaign: { address } } = campaignPayload

  const activitiesPayload = useActivities({
    filters: {
      recipient_type: 'Campaign',
      recipient_id: campaign.id,
      ...pageFilters,
    },
    performHttpRequests: !!campaign.id,
    requestOptions: defaultRequestOptions.activity,
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredActivities,
    filteredActivitiesCount,
    hasFilteredActivities,
    loading,
  } = activitiesPayload

  const renderLoadMoreMobileView = isMobile && canLoadMore

  return (
    <>
      <Helmet>
        <title>{address || 'Campaign'} | Activity</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey="activities"
        nestedNavigation
        showBackButton
        tabBarItems={[
          {
            href: pathWithRedirect(`#/campaigns/${campaign.id}/edit`),
            icon: faHome,
            key: 'dashboard',
            title: 'Dashboard',
          },
          // @ts-expect-error TS2741
          {
            icon: faClockRotateLeft,
            key: 'activities',
            title: 'Activity',
          },
        ]}
        title="Activity"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.campaign.activity.banner" />

            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredActivities && <ActivityBlankState />}

            {!loading && hasFilteredActivities && (
              <Box flexDirection="column">
                <DashboardModule
                  contentBoxProps={{ flexDirection: 'column' }}
                  title="Activity"
                >
                  <DashboardModule.ScrollableSection
                    // @ts-expect-error TS2322
                    alignItems={hasFilteredActivities ? undefined : 'center'}
                    flexDirection="column"
                    height="100%"
                    justifyContent={hasFilteredActivities ? undefined : 'center'}
                    marginTop={0}
                    maxHeight="800px"
                  >
                    {filteredActivities.map((activity, index) => (
                      <ActivityItem
                        activity={activity}
                        boxProps={{ padding: 'large' }}
                        isLastActivity={index === (filteredActivitiesCount - 1)}
                        key={activity.id}
                      />
                    ))}
                  </DashboardModule.ScrollableSection>
                </DashboardModule>

                {(!isMobile || renderLoadMoreMobileView) && (
                  <Box variant="white" marginBottom={['large', 0]}>
                    <DashboardModule.LoadMoreFooter
                      callbacks={{ loadMore }}
                      canLoadMore={canLoadMore}
                      entityCount={filteredActivitiesCount}
                      loading={loading}
                      showBorderTop={false}
                    />
                  </Box>
                )}
              </Box>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.campaign.activity.sidebar" />

            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
